import type { AlertRuleSpec, ServerMessage, WrappedCoin } from "@screener/protocol";
import { instrumentKey } from "@/density/cards";
import {
  alertTriggerLabel,
  formatUsd,
  sideLabel,
  tickerLabel,
  venueOf,
  wrappedMark,
} from "@/lib/labels";

/**
 * Чистая логика §8 (1.20): что делает вкладка на срабатывание правила.
 * Флаги действий (`sound`/`notify`) сообщение `alert_fired` не несёт —
 * §7.6 (1.20) называет его состав («правило, триггер, инструмент, сторону
 * и величину»), и добавлять их значило бы менять контракт (design.md
 * решение 1). Их берёт из правил пользователя вызывающий (`use-alert-actions`),
 * здесь только решение по уже прочитанной карте.
 */

export type AlertFiredMessage = Extract<ServerMessage, { type: "alert_fired" }>;

/**
 * Одно правило `GET /api/alert-rules` в терпимом разборе `alert-rules-api.ts`
 * (ревью stage6-alert-sound, пункт 4): вкладке нужны только `id` и флаги
 * действий, не вся форма `AlertRuleSpec`.
 */
export type AlertRuleActions = Pick<AlertRuleSpec, "sound" | "notify"> & { id: string };

/** Флаги действий одного правила — то, что вкладка выполняет сама (§8 1.20). */
interface RuleActionFlags {
  sound: boolean;
  notify: boolean;
}

/** Карта `rule_id → флаги действий`, прочитанная запросным каналом §7.6. */
export type AlertRulesMap = ReadonlyMap<string, RuleActionFlags>;

/** Строит карту флагов из списка правил `GET /api/alert-rules` (§7.6). */
export function rulesMap(rules: readonly AlertRuleActions[]): AlertRulesMap {
  const map = new Map<string, RuleActionFlags>();
  for (const rule of rules) {
    map.set(rule.id, { sound: rule.sound, notify: rule.notify });
  }
  return map;
}

/**
 * Действия сработавшего правила по уже прочитанной карте. `"unknown"` —
 * правило, которого в карте нет: вызывающий по спеке `web-alert-sound`
 * («Действия правила вкладка знает из правил пользователя») обязан
 * перечитать правила и повторить попытку, а не молчать сразу.
 */
export function actionsOf(
  rules: AlertRulesMap,
  message: AlertFiredMessage,
): RuleActionFlags | "unknown" {
  return rules.get(message.rule_id) ?? "unknown";
}

/**
 * Ключ срабатывания для дедупликации между вкладками (design.md решение 4).
 * `seq` в ключ намеренно не входит: номер свой у каждого соединения
 * (§7.6 1.20), и в двух вкладках одно и то же срабатывание несёт разные
 * номера — ключ, включающий `seq`, никогда бы не совпал между вкладками.
 */
export function alertKey(message: AlertFiredMessage): string {
  const { instrument } = message;
  return [
    message.rule_id,
    message.trigger,
    instrument.exchange,
    instrument.market,
    instrument.base,
    instrument.quote,
    message.side,
    message.usd,
  ].join("|");
}

/** Заголовок и тело системной нотификации §8/§9 (1.20). */
export interface AlertNotificationText {
  title: string;
  body: string;
}

export function notificationText(
  message: AlertFiredMessage,
  wrapped: readonly WrappedCoin[] = [],
  exchangeBases: ReadonlyMap<string, string> = new Map(),
): AlertNotificationText {
  const { instrument } = message;
  const mark = wrappedMark(instrument.base, wrapped);
  // Срабатывание базы биржи не несёт: она берётся из индекса живых снимков
  // (`StreamState.exchange_bases`); нет записи — приведённая база (§63 п. 5).
  const ticker = tickerLabel(instrument, exchangeBases.get(instrumentKey(instrument)) ?? "");
  const pair = mark === undefined ? ticker : `${ticker} ${mark}`;
  return {
    title: `${pair} · ${venueOf(instrument)}`,
    body: `${sideLabel(message.side)} · ${alertTriggerLabel(message.trigger)} · $${formatUsd(message.usd)}`,
  };
}

/** Есть ли среди правил хотя бы одно со включённым звуком (для шапки §9 1.20). */
export function needsSound(rules: AlertRulesMap): boolean {
  for (const actions of rules.values()) {
    if (actions.sound) {
      return true;
    }
  }
  return false;
}

/** Есть ли среди правил хотя бы одно со включённой нотификацией (для шапки §9 1.20). */
export function needsNotify(rules: AlertRulesMap): boolean {
  for (const actions of rules.values()) {
    if (actions.notify) {
      return true;
    }
  }
  return false;
}
